import type { Observable, Subscription } from "rxjs";

import { type Deferred, defer } from "./defer";

const COMPLETE = Symbol("COMPLETE");
export { defer } from "./defer";

/**
 * Transforms an Observable<T> into a AsyncIterableIterator<T>.
 */
export async function* observableToAsyncGenerator<Value>(
	observable: Observable<Value>
): AsyncIterableIterator<Value> {
	//
	// THEORY OF OPERATION.
	//
	// RxJS `Observable<T>` is an implementation of the publish/subscribe design pattern. When
	// observers subscribe, they receive notifications of events eagerly when they are produced.
	// Because events are processed eagerly, we say `Observable<T>` is a "push-based" eventing
	// semantics.
	//
	// The EcmaScript standard `AsyncIterableIterator<T>`, in contrast, is "pull-based"—events are
	// represented by a sequence of `Promise`s which completely suspend the thread and resolve only
	// when a new value becomes available.
	//
	// To transform an `Observable<T>` into an `AsyncIterableIterator<T>`, something needs to sit
	// between the two and act as a bridge. This bridge will:
	//
	//   - Hold a `Promise` open until the `Observable<T>` emits a value.
	//   - If the `Observable<T>` produces many values before they can be consumed, "buffer" them in
	//     a `Promise[]` so they can be consumed one after another by the `AsyncIterableIterator<T>`
	//     consumer.
	//   - Handle all errors and completion events.
	//
	// To accomplish this, this function does the following, in this order.
	//
	//   1. Initialize a `Deferred[]`. A `Deferred` is basically a `Promise` that blocks until the
	//      `Observable<T>` publishes a value `t` and we will call `deferred.resolve(t)`. The
	//      `AsyncIterableIterator<T>` is simply waiting for this `Promise` to resolve, and once it
	//      does, it instantly yields the value to its consumer.
	//   2. Asynchronously `subscribe` to the `Observable<T>`. Each time the `Observable<T>`
	//      produces a value `t`, we call `deferred.resolve(t)`, causing the
	//      `AsyncIterableIterator<T>` to advance.
	//   3. Loop through the `Deferred[]`, `await`ing each one to resolve and `yield`ing its value.
	//      The `yield` and `await` are what makes this an `AsyncIterableIterator<T>`.
	//   4. On completion or error, unsubscribe and return.
	//

	let subscription: Subscription | undefined;
	try {
		const deferred: Deferred<Value | typeof COMPLETE>[] = [defer()];

		subscription = observable.subscribe({
			next(data) {
				const n = deferred[deferred.length - 1];
				deferred.push(defer());
				n?.resolve(data);
			},
			error(err) {
				deferred[deferred.length - 1]?.reject(err);
			},
			complete() {
				const n = deferred[deferred.length - 1];
				n?.resolve(COMPLETE);
			},
		});

		while (true) {
			const n = deferred[0];
			if (!n) {
				break;
			}

			const value = await n;
			void deferred.shift();
			if (value === COMPLETE) {
				break;
			}
			yield value;
		}
	} finally {
		subscription?.unsubscribe();
	}
}
